/**
 * Request queue utilities for handling rate-limited requests.
 *
 * Queues API requests when rate limits are reached and processes them
 * when tokens become available. Prevents request loss for rapid user actions.
 *
 * @module requestQueue
 */

import { checkRateLimit, type RateLimitConfig } from './rateLimiter';
import { logger } from './logger';

/**
 * Queued request with metadata
 */
interface QueuedRequest {
  /** Unique request ID */
  readonly id: string;
  /** Request function to execute */
  readonly execute: () => Promise<unknown>;
  /** HTTP method for rate limiting */
  readonly method: string;
  /** Rate limit config for this request */
  readonly rateLimitConfig?: RateLimitConfig;
  /** Resolve function for the request promise */
  readonly resolve: (value: unknown) => void;
  /** Reject function for the request promise */
  readonly reject: (error: unknown) => void;
  /** Timestamp when request was queued */
  readonly queuedAt: number;
}

/**
 * Maximum queue size per endpoint type
 */
const MAX_QUEUE_SIZE = 5;

/**
 * Maximum age for a queued request before it's considered stale (5 minutes)
 * Requests older than this will be rejected to prevent indefinite queuing
 */
const MAX_REQUEST_AGE = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Interval for periodic cleanup of stale queues (30 seconds)
 */
const CLEANUP_INTERVAL = 30 * 1000; // 30 seconds in milliseconds

/**
 * Request queues per HTTP method
 */
const requestQueues = new Map<string, QueuedRequest[]>();

/**
 * Queue processing intervals per method
 */
const processingIntervals = new Map<string, ReturnType<typeof setInterval>>();

/**
 * Global cleanup interval (runs periodically to clean up stale queues)
 */
let globalCleanupInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Generate unique request ID
 */
function generateRequestId(): string {
  return `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Check if a request has exceeded the maximum age and is considered stale
 * @param request - The queued request to check
 * @returns True if the request is stale and should be rejected
 */
function isRequestStale(request: QueuedRequest): boolean {
  const age = Date.now() - request.queuedAt;
  return age > MAX_REQUEST_AGE;
}

/**
 * Clean up stale queues and remove empty queues from the Map
 * This function runs periodically to prevent memory leaks
 */
function cleanupStaleQueues(): void {
  const now = Date.now();
  let totalStaleRejected = 0;
  let totalEmptyRemoved = 0;

  // Process each queue
  for (const [method, queue] of requestQueues.entries()) {
    if (!queue || queue.length === 0) {
      // Remove empty queue from Map
      requestQueues.delete(method);
      totalEmptyRemoved++;
      continue;
    }

    // Check for stale requests and reject them
    const staleIndices: number[] = [];
    queue.forEach((request, index) => {
      if (isRequestStale(request)) {
        staleIndices.push(index);
      }
    });

    // Reject stale requests (process in reverse to maintain indices)
    for (let i = staleIndices.length - 1; i >= 0; i--) {
      const index = staleIndices[i];
      const staleRequest = queue[index];
      staleRequest.reject(
        new Error(
          `Request queued for ${Math.round((now - staleRequest.queuedAt) / 1000)}s exceeded maximum age of ${MAX_REQUEST_AGE / 1000}s`,
        ),
      );
      queue.splice(index, 1);
      totalStaleRejected++;
    }

    // If queue is now empty after removing stale requests, remove it from Map
    if (queue.length === 0) {
      requestQueues.delete(method);
      totalEmptyRemoved++;

      // Also stop processing interval if it exists
      const intervalId = processingIntervals.get(method);
      if (intervalId) {
        clearInterval(intervalId);
        processingIntervals.delete(method);
      }
    }
  }

  // Log cleanup activity if anything was cleaned up
  if (totalStaleRejected > 0 || totalEmptyRemoved > 0) {
    logger.debug('Request queue cleanup', {
      staleRejected: totalStaleRejected,
      emptyQueuesRemoved: totalEmptyRemoved,
    });
  }
}

/**
 * Start global periodic cleanup if not already running
 */
function startGlobalCleanup(): void {
  if (globalCleanupInterval !== null) {
    return; // Already running
  }

  globalCleanupInterval = setInterval(() => {
    cleanupStaleQueues();
  }, CLEANUP_INTERVAL);

  logger.debug('Started global request queue cleanup interval');
}

/**
 * Process queue for a specific method
 */
async function processQueue(method: string): Promise<void> {
  const queue = requestQueues.get(method);
  if (!queue || queue.length === 0) {
    // Stop processing if queue is empty
    const intervalId = processingIntervals.get(method);
    if (intervalId) {
      clearInterval(intervalId);
      processingIntervals.delete(method);
    }
    // Remove empty queue from Map to prevent memory leak
    requestQueues.delete(method);
    return;
  }

  const firstRequest = queue[0];

  // Check if request is stale before processing
  if (isRequestStale(firstRequest)) {
    queue.shift();
    firstRequest.reject(
      new Error(
        `Request queued for ${Math.round((Date.now() - firstRequest.queuedAt) / 1000)}s exceeded maximum age of ${MAX_REQUEST_AGE / 1000}s`,
      ),
    );
    // Continue processing remaining queue
    await processQueue(method);
    return;
  }

  const rateLimitCheck = checkRateLimit(method, firstRequest.rateLimitConfig);

  if (rateLimitCheck.allowed) {
    // Remove from queue and execute
    queue.shift();

    try {
      const result = await firstRequest.execute();
      firstRequest.resolve(result);
    } catch (error) {
      firstRequest.reject(error);
    }

    // Continue processing queue
    await processQueue(method);
  } else {
    // Rate limit still active, wait and retry
    // The interval will call this again
  }
}

/**
 * Start queue processing for a method if not already started
 */
function startQueueProcessing(method: string): void {
  if (processingIntervals.has(method)) {
    return; // Already processing
  }

  // Start global cleanup if not already running
  startGlobalCleanup();

  // Process queue every 100ms
  const intervalId = setInterval(() => {
    processQueue(method).catch((error) => {
      // Log error but don't break queue processing
      logger.error(`Error processing queue for ${method}:`, error);
    });
  }, 100);

  processingIntervals.set(method, intervalId);
}

/**
 * Queue a request for later execution
 *
 * @param method - HTTP method
 * @param execute - Function to execute when rate limit allows
 * @param rateLimitConfig - Optional rate limit config override
 * @returns Promise that resolves when request is executed
 */
export function queueRequest(
  method: string,
  execute: () => Promise<unknown>,
  rateLimitConfig?: RateLimitConfig,
): Promise<unknown> {
  const queue = requestQueues.get(method) || [];
  requestQueues.set(method, queue);

  // Check if queue is full
  if (queue.length >= MAX_QUEUE_SIZE) {
    return Promise.reject(
      new Error(
        `Request queue is full (${MAX_QUEUE_SIZE} requests). Please wait before retrying.`,
      ),
    );
  }

  // Create queued request
  return new Promise((resolve, reject) => {
    const queuedRequest: QueuedRequest = {
      id: generateRequestId(),
      execute,
      method,
      rateLimitConfig,
      resolve,
      reject,
      queuedAt: Date.now(),
    };

    queue.push(queuedRequest);

    // Start processing if not already started
    startQueueProcessing(method);
  });
}

/**
 * Get queue status for a method
 *
 * @param method - HTTP method
 * @returns Queue length and oldest request age
 */
export function getQueueStatus(method: string): {
  length: number;
  oldestRequestAge: number | null;
} {
  const queue = requestQueues.get(method) || [];
  const oldestRequest = queue[0];

  return {
    length: queue.length,
    oldestRequestAge: oldestRequest ? Date.now() - oldestRequest.queuedAt : null,
  };
}

/**
 * Clear queue for a method (useful for error recovery or testing)
 *
 * @param method - HTTP method to clear, or undefined to clear all
 */
export function clearQueue(method?: string): void {
  if (method) {
    const queue = requestQueues.get(method);
    if (queue) {
      // Reject all queued requests
      queue.forEach((request) => {
        request.reject(new Error('Request queue cleared'));
      });
      requestQueues.delete(method);
    }

    // Stop processing
    const intervalId = processingIntervals.get(method);
    if (intervalId) {
      clearInterval(intervalId);
      processingIntervals.delete(method);
    }
  } else {
    // Clear all queues
    requestQueues.forEach((queue) => {
      queue.forEach((request) => {
        request.reject(new Error('Request queue cleared'));
      });
    });
    requestQueues.clear();

    // Stop all processing
    processingIntervals.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    processingIntervals.clear();

    // Stop global cleanup if no queues remain
    if (globalCleanupInterval !== null) {
      clearInterval(globalCleanupInterval);
      globalCleanupInterval = null;
      logger.debug('Stopped global request queue cleanup interval');
    }
  }
}

